import { DocumentCategoryValueType } from '@smiles/core-account/dist/Types/CommonTypes';
import { SmlsInputText } from '@smiles/smiles-ui-kit-react';
import { useEmailRecaptcha } from 'pages/RedoRegisterTangie/context/EmailRecaptchaContext';
import { useEmailSharedValidation } from 'pages/RedoRegisterTangie/context/EmailSharedValidationContext';
import { useMemberData } from 'pages/RedoRegisterTangie/context/MemberDataContext';
import { isValidEmail } from 'pages/RedoRegister/helpers';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from 'services/core/services';

export function ConfirmEmailInput() {
  const { t } = useTranslation('redo-register-v2');

  const { handler, data, getMemberData, formData, ...memberData } = useMemberData();
  const { confirmEmailInput, events } = useEmailSharedValidation();
  const recaptcha = useEmailRecaptcha();

  const [value, setValue] = useState('');
  const [errorLabel, setErrorLabel] = useState('');

  const resetInputValue = () => {
    setValue('');
  };

  useEffect(() => {
    confirmEmailInput.setResetCallback(() => resetInputValue);
  }, []);

  const callValidateEmail = useCallback(async () => {
    confirmEmailInput.resetError();
    if (recaptcha.isVisible && !recaptcha.recaptchaValue) {
      recaptcha.showValidationError();
      return;
    }

    try {
      const response = await validateEmail({
        document: data.document as DocumentCategoryValueType,
        email: value,
        recaptcha: recaptcha.recaptchaValue,
      });

      if (!response.success) {
        if (response.errors?.details?.length) {
          const error = response.errors.details[0];

          if (error.errorMessage === 'Recaptcha inválido' && error.errorCode === '400') {
            recaptcha.setEnableRecaptcha(true);
            recaptcha.showRecaptcha();
          }
        }

        throw new Error('[validateEmail] Request not successful');
      }

      recaptcha.setEnableRecaptcha(response.data.enableRecaptcha);

      if (response.data.emailAlreadyUsed && Number(response.data.code) !== 6) {
        events.validateAlreadyInUse();
        return;
      }

      handler({ confirmEmail: value });
    } catch (e) {
      events.validateEmailFailed();
      console.log('[validateEmail] Error:', e);
    }
  }, [data.document, value, recaptcha.recaptchaValue, recaptcha.isVisible]);

  useEffect(() => {
    if (!!recaptcha.recaptchaValue && recaptcha.isVisible) {
      callValidateEmail();
    }
  }, [recaptcha.recaptchaValue, recaptcha.isVisible]);

  const handleValidate = useCallback(
    (inputStr: string) => {
      if (getMemberData.email === value) {
        return true;
      }

      const result = isValidEmail(value);

      if (!result) {
        setErrorLabel('errors.invalid_email');
        handler({
          confirmEmail: '',
        });
        return result;
      }

      if (value !== formData.email) {
        handler({ confirmEmail: '' });
        return false;
      }

      setErrorLabel('');

      if (recaptcha.isVisible && !!recaptcha.recaptchaValue) {
        recaptcha.resetRecaptcha();
        return result;
      }

      callValidateEmail();
      return result;
    },
    [value, formData.email, recaptcha.isVisible, recaptcha.recaptchaValue],
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('');

    setErrorLabel('');
    setValue(e.target.value);
    memberData.setFormEdit();
  };

  const handleOnDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    return false;
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    return false;
  };

  const errorMessage = confirmEmailInput.forceError
    ? t(confirmEmailInput.errorMessage)
    : t(errorLabel) || t('errors.invalid_email_not_match');

  useEffect(() => {
    if (!memberData.response.ready) {
      return;
    }

    if (memberData.response.ready && getMemberData.email === 'mudar@mudar.com.br') {
      setValue('');
      return;
    }

    setValue(getMemberData.email);
  }, [memberData.response]);

  return (
    <div data-testid="first-access-confirm-email-container">
      <SmlsInputText
        id="redo-register-confirm-email-input"
        data-testid="first-access-confirm-email-input"
        autoComplete="off"
        placeholder={t('placeholders.confirm_email')}
        maxLength={100}
        defaultValue={value}
        regex={/[^.a-zA-Z0-9@_-]+|@(.*)@/g}
        debouncedTime={700}
        inputMode="email"
        textLabel={t('labels.confirm_email')}
        value={value}
        forceError={confirmEmailInput.forceError}
        errorMessage={errorMessage}
        validate={handleValidate}
        onChange={handleOnChange}
        onDrop={handleOnDrop}
        onPaste={handleOnPaste}
      />
    </div>
  );
}
